import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NavLink } from 'src/design/atoms/NavLink';
import { useAuth } from 'src/hooks/useAuth';
import { useDialogs } from 'src/hooks/useDialogs';
import { useMenu } from 'src/hooks/useMenu';
import { INavigationItem } from 'src/app/navigation/navigationTypes';

export interface INavItemProps {
  item: INavigationItem;
  level: number;
}

/** @registry Sidebar leaf nav link: resolves job-scoped urls, active-state highlighting, and mobile drawer auto-close. */
export const NavItem: React.FC<INavItemProps> = ({ item, level }) => {
  const { checkFeatureAllowed } = useAuth();
  const { pathname } = useLocation();
  const { setCreateProjectOpen } = useDialogs();
  const { drawerOpen, closeDrawerIfMobile } = useMenu();

  const isAllowed = item.allowAll
    ? checkFeatureAllowed(item.requiredScopes)
    : checkFeatureAllowed(item.requiredScopes, item.id);

  if (!isAllowed && !item.allowAll) return null;

  const targetUrl = item.isJob
    ? `/jobs/${pathname.split('/')[2]}${item.url}`
    : item.url;

  const isActive = targetUrl ? pathname.startsWith(targetUrl) : false;

  const handleClick = (e: React.MouseEvent) => {
    if (item.isCreateJob) {
      // "Create Job" opens a modal instead of navigating — leave the drawer
      // state alone so the modal stacks cleanly on top of the sidebar.
      e.preventDefault();
      setCreateProjectOpen(true);
      return;
    }
    // Auto-collapse the drawer after a mobile user taps a nav item so the
    // newly-loaded page is actually visible. Skip for inter-module links
    // (e.g. "Back to Dashboard") that swap the menu itself.
    if (!item.keepDrawerOpen) {
      closeDrawerIfMobile();
    }
  };

  return (
    <NavLink
      component={Link}
      to={targetUrl}
      label={
        drawerOpen || (!drawerOpen && level !== 1) ? item.title : undefined
      }
      leftSection={item.icon}
      active={isActive}
      disabled={item.disabled}
      onClick={handleClick}
      pl={drawerOpen ? level * 28 : undefined}
      py={5} // We want a smaller padding than Mantine's standard sizes allow.
    />
  );
};
